<template>
  <transition-group
    name="notice"
    tag="div"
    class="notice__wrapper"
  >
    <div
      v-for="toast in toasts"
      :key="`notice-${toast.id}`"
      :class="[`notice--${toast.type}`]"
      class="notice"
      role="status"
    >
      <i
        :class="iconOf(toast)"
        class="notice__icon"
      />
      <span class="notice__text">{{ toast.text }}</span>
      <button
        type="button"
        class="notice__close"
        :aria-label="$t('system.btn_close')"
        @click="hide(toast.id)"
      >
        ×
      </button>
    </div>
  </transition-group>
</template>

<script>
/**
 * Короткие сообщения над нижней панелью: приложение что-то сделало или
 * отказалось делать, сказать надо, спрашивать не о чем.
 *
 * Очередь живёт в сторе — сообщение кладут перед переходом, а читают уже на
 * другом экране. Разбор — docs/toast-notifications.md.
 *
 * Стопка уведомлений PWA живёт отдельно, в соседнем systemNotification: она
 * работает, и переносить её сюда незачем.
 *
 * Классы называются notice, а не toast: `.toast` занят бутстрапом, и там у него
 * `display: none`, пока нет `.show`. Сообщение просто не рисовалось.
 */

// Шести секунд хватает на две строки; меньше — не успеть прочесть, больше —
// тост начинает мешать.
const TOAST_LIFETIME = 6000;

// Иконка по роду сообщения. Кто знает лучше — присылает свою: например у связи
// это wi-fi, и он говорит о причине точнее восклицательного знака.
const TYPE_ICON = {
  info: 'exu-info-circle',
  success: 'exu-check',
  danger: 'exu-annotation-alert',
};

export default {
  name: 'ToastList',
  data() {
    return {
      // таймеры по id: снимаем их, если тост закрыли крестиком раньше срока
      timers: {},
    };
  },
  computed: {
    toasts() {
      return this.$store.getters.getToasts;
    },
  },
  watch: {
    toasts: {
      deep: true,
      handler(list) {
        list.forEach((toast) => {
          // sticky снимает не таймер, а тот, кто его повесил: отсутствие связи
          // держится, пока связь не вернётся
          if (toast.sticky || this.timers[toast.id]) return;
          this.timers[toast.id] = setTimeout(() => this.hide(toast.id), TOAST_LIFETIME);
        });
      },
    },
  },
  beforeUnmount() {
    Object.values(this.timers).forEach((timer) => clearTimeout(timer));
  },
  methods: {
    iconOf(toast) {
      return toast.icon || TYPE_ICON[toast.type] || TYPE_ICON.info;
    },
    hide(id) {
      if (this.timers[id]) {
        clearTimeout(this.timers[id]);
        delete this.timers[id];
      }
      this.$store.commit('removeToast', id);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/style/variables.scss';

.notice {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  max-width: 760px;
  margin: 0 auto 6px;
  padding: 12px 8px 12px 16px;
  color: #2c3e50;
  font-size: 14px;
  line-height: 20px;
  background: #fefefe;
  border-radius: 16px;
  box-shadow: 0 0 10px 3px rgba(0, 0, 0, 0.25);
  // тип отличает полоса слева: сплошная заливка под сообщением о сбое связи
  // избыточна и хуже читается
  border-left: 4px solid #356899;

  &--danger {
    border-left-color: $color-accent;
  }
  &--success {
    border-left-color: #50AA50;
  }

  // Цвет берёт у полосы слева — тот же признак рода сообщения, только заметнее
  &__icon {
    flex: 0 0 auto;
    margin-top: 1px;
    color: #356899;
    font-size: 18px;
    line-height: 18px;
  }
  &--danger &__icon {
    color: $color-accent;
  }
  &--success &__icon {
    color: #50AA50;
  }

  &__text {
    flex: 1 1 auto;
  }

  // Закрывает только крестик. Раньше гасило нажатие по всему тосту — с явной
  // кнопкой это лишнее: случайное касание убирало бы недочитанное сообщение.
  &__close {
    flex: 0 0 auto;
    width: 28px;
    height: 28px;
    // Отрицательные поля сверху и снизу убирают кнопку из расчёта высоты:
    // палец получает свои 28px, а строка остаётся ростом со строку текста.
    // Иначе кнопка задирала высоту, и под текстом появлялось лишних 4px.
    margin: -4px 0;
    padding: 0;
    color: #707176;
    font-size: 20px;
    line-height: 1;
    border: none;
    background: transparent;
  }

  // Обёртка живёт всегда, иначе последний тост исчезал бы вместе с ней, не
  // успев проиграть уход. Пустая она нулевой высоты: отступы на самих тостах.
  &__wrapper {
    position: relative;
    padding: 0 12px;
    pointer-events: none;

    .notice {
      pointer-events: auto;
    }
  }

  // Появление снизу вверх, уход — туда же. Сдвиг небольшой: тост и так
  // возникает у края экрана, длинный проезд читается как рывок.
  &-enter-active,
  &-leave-active,
  &-move {
    transition: opacity .2s ease, transform .2s ease;
  }
  &-enter-from,
  &-leave-to {
    opacity: 0;
    transform: translateY(12px);
  }
  // уходящий вынут из потока, иначе соседи дёргаются раньше времени
  &-leave-active {
    position: absolute;
    width: calc(100% - 24px);
  }
}
</style>
